import { useAgGridDefaults } from "@/lib/ag-grid-defaults";
import { useServerGrid } from "@/lib/use-server-grid";
import { useWorkspacePage } from "@/components/workspace/use-workspace-page";
import {
	useApprovePlannedAction,
	useCancelPlannedAction,
} from "@/hooks/use-manufacturing";
import { manufacturingClient } from "@/lib/manufacturing-client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { AgGridReact } from "@ag-grid-community/react";
import type { ColDef, ICellRendererParams } from "@ag-grid-community/core";
import type { PlannedAction } from "@/gen/omnios/v1/manufacturing_pb";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/manufacturing/actions")({
	component: ActionsPage,
});

function UrgencyCell({ value }: ICellRendererParams) {
	const color =
		value === "critical"
			? "bg-red-500"
			: value === "high"
				? "bg-amber-500"
				: value === "normal"
					? "bg-blue-400"
					: "bg-gray-400";
	return (
		<span className="flex items-center gap-1.5">
			<span className={`h-2 w-2 rounded-full ${color}`} />
			<span className="capitalize">{value}</span>
		</span>
	);
}

function StatusCell({ value }: ICellRendererParams) {
	const variant =
		value === "planned"
			? "secondary"
			: value === "approved"
				? "default"
				: value === "executed"
					? "outline"
					: "destructive";
	return <Badge variant={variant}>{value}</Badge>;
}

function ActionsPage() {
	const { t } = useTranslation("manufacturing");
	const navigate = useNavigate();
	const gridDefaults = useAgGridDefaults();
	const approve = useApprovePlannedAction();
	const cancelAction = useCancelPlannedAction();
	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	const columns = useMemo<ColDef[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 40,
				pinned: "left",
				sortable: false,
				filter: false,
				resizable: false,
			},
			{
				field: "urgency",
				headerName: t("field_urgency"),
				width: 120,
				cellRenderer: UrgencyCell,
				filter: false,
				pinned: "left",
			},
			{
				field: "actionType",
				headerName: t("field_action_type"),
				width: 110,
				valueFormatter: (p: { value: string }) =>
					t(`type_${p.value}`, { defaultValue: p.value }),
			},
			{
				field: "productId",
				headerName: t("field_product"),
				width: 130,
				valueFormatter: (p: { value: string }) => p.value?.slice(0, 8) ?? "",
			},
			{
				field: "qty",
				headerName: t("field_qty"),
				width: 100,
				type: "numericColumn",
				filter: "agNumberColumnFilter",
				valueFormatter: (p: { value: number }) =>
					Number(p.value).toLocaleString("ru-RU"),
			},
			{
				field: "targetDate",
				headerName: t("field_target_date"),
				width: 110,
			},
			{
				field: "orderByDate",
				headerName: t("field_order_by_date"),
				width: 110,
			},
			{
				field: "supplierId",
				headerName: t("field_supplier"),
				width: 130,
				valueFormatter: (p: { value: string }) =>
					p.value ? p.value.slice(0, 8) : "—",
			},
			{
				field: "estimatedCostCents",
				headerName: t("field_cost"),
				width: 120,
				type: "numericColumn",
				filter: "agNumberColumnFilter",
				valueFormatter: (p: { value: number }) =>
					p.value > 0
						? `₽${(p.value / 100).toLocaleString("ru-RU")}`
						: "—",
			},
			{
				field: "confidence",
				headerName: t("field_confidence"),
				width: 90,
				filter: "agNumberColumnFilter",
				valueFormatter: (p: { value: number }) =>
					`${(p.value * 100).toFixed(0)}%`,
			},
			{
				field: "status",
				headerName: t("field_status"),
				width: 120,
				cellRenderer: StatusCell,
				filter: false,
			},
		],
		[t],
	);

	const { datasource, totalRows, refreshGrid, gridRef } = useServerGrid<
		PlannedAction & { id: string }
	>({
		fetchRows: async ({ pageSize, pageToken }) => {
			const resp = await manufacturingClient.listPlannedActions({
				page: { pageSize, pageToken },
				productId: "",
				status: "",
				urgency: "",
			});
			return {
				rows: resp.actions as (PlannedAction & { id: string })[],
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		pageSize: 100,
		colToField: {
			actionType: "action_type",
			productId: "product_id",
			targetDate: "target_date",
			orderByDate: "order_by_date",
			supplierId: "supplier_id",
			estimatedCostCents: "estimated_cost_cents",
		},
	});

	const handleApprove = useCallback(() => {
		for (const id of selectedIds) {
			approve.mutate({ id });
		}
		setSelectedIds([]);
	}, [selectedIds, approve]);

	const statusBar = useMemo(
		() => [
			{
				id: "count",
				content: `${totalRows} ${t("status_bar_rows")}`,
				align: "left" as const,
			},
			{
				id: "selected",
				content:
					selectedIds.length > 0
						? `${selectedIds.length} ${t("status_bar_selected")}`
						: "",
				align: "right" as const,
			},
		],
		[totalRows, selectedIds, t],
	);

	useWorkspacePage({ statusBar });

	return (
		<div className="flex h-full flex-col">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption(
								"quickFilterText",
								e.target.value,
							)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => {
						for (const id of selectedIds) {
							cancelAction.mutate({ id, reason: "Отменено планировщиком" });
						}
						setSelectedIds([]);
					}}
					disabled={selectedIds.length === 0}
				>
					{t("action_cancel")}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => navigate({ to: "/manufacturing/board" })}
				>
					{t("board")}
				</Button>
				<Button
					size="sm"
					className="h-9"
					onClick={handleApprove}
					disabled={selectedIds.length === 0}
				>
					{t("action_approve")}
				</Button>
			</div>
			<div className="flex-1 ag-theme-quartz">
				<AgGridReact
					ref={gridRef}
					{...gridDefaults}
					columnDefs={columns}
					rowModelType="infinite"
					datasource={datasource}
					cacheBlockSize={100}
					maxBlocksInCache={20}
					rowSelection="multiple"
					suppressRowClickSelection
					rowClass="cursor-pointer"
					onSelectionChanged={() => {
						const selected = gridRef.current?.api
							?.getSelectedRows()
							.map((r: { id: string }) => r.id);
						setSelectedIds(selected ?? []);
					}}
					onSortChanged={() => refreshGrid()}
					onFilterChanged={() => refreshGrid()}
					onRowClicked={(e) => {
						if (e.data?.productId) {
							navigate({
								to: "/manufacturing/products/$id",
								params: { id: e.data.productId },
							});
						}
					}}
					getRowId={(params) => params.data?.id}
				/>
			</div>
		</div>
	);
}
